import React, { useEffect } from 'react';
import {
    X, CheckCircle2, Clock, AlertTriangle, FileText, FileCheck, User, IdCard, Mail, MessageSquare,
    ShieldCheck, Archive, Send, Eye, PenLine, ExternalLink, MapPin, Fingerprint,
} from 'lucide-react';

/**
 * Scheda leggibile di una pratica OtpService (dossier) a partire dal JSON dell'API.
 * I campi assenti vengono omessi e il codice OTP non viene mai mostrato.
 */

// ── Formattazione ───────────────────────────────────────────────────────────
const fmtDateTime = (v?: string, seconds = false) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        ...(seconds ? { second: '2-digit' } : {}),
    });
};
/** Date "solo giorno" (yyyy-mm-dd): niente conversione di fuso. */
const fmtDay = (v?: string) => {
    if (!v) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};
const name = (o: any) => o?.name || '';

export const SIGN_TYPE_LABEL: Record<string, string> = {
    advanced_sign: 'Firma Elettronica Avanzata (FEA) con codice OTP',
    simple_sign: 'Firma elettronica semplice',
    qualified_sign: 'Firma Elettronica Qualificata',
};
const DOSSIER_STATE: Record<string, { label: string; tone: Tone }> = {
    draft: { label: 'Bozza', tone: 'slate' },
    just_created: { label: 'Appena creata', tone: 'slate' },
    notified: { label: 'Inviata al firmatario, in attesa di firma', tone: 'amber' },
    viewed: { label: 'Visualizzata dal firmatario', tone: 'amber' },
    signing: { label: 'Firma in corso', tone: 'amber' },
    signed: { label: 'Firmata', tone: 'emerald' },
    completed: { label: 'Firmata e completata', tone: 'emerald' },
    rejected: { label: 'Rifiutata', tone: 'rose' },
    refused: { label: 'Rifiutata', tone: 'rose' },
    expired: { label: 'Scaduta', tone: 'rose' },
    canceled: { label: 'Annullata', tone: 'slate' },
    cancelled: { label: 'Annullata', tone: 'slate' },
};
const RECIPIENT_STATE: Record<string, string> = {
    notified: 'Avvisato, non ha ancora firmato',
    viewed: 'Ha aperto il documento',
    accepted: 'Ha accettato le condizioni',
    validating: 'Codice OTP in verifica',
    signed: 'Ha firmato',
    signed_document_sent: 'Ha firmato · copia firmata inviata',
    rejected: 'Ha rifiutato',
    refused: 'Ha rifiutato',
};
const IDENT: Record<string, { label: string; ok: boolean }> = {
    recognized_de_visu: { label: 'Identità verificata di persona (de visu)', ok: true },
    recognized: { label: 'Identità verificata', ok: true },
    recognized_remotely: { label: 'Identità verificata da remoto', ok: true },
    unrecognized: { label: 'Identità non ancora verificata', ok: false },
    to_be_validated: { label: 'Riconoscimento da validare', ok: false },
    waiting_validation: { label: 'Riconoscimento in attesa di validazione', ok: false },
    rejected: { label: 'Riconoscimento rifiutato', ok: false },
};
const DOC_TYPE: Record<string, string> = {
    identification_card: "Carta d'identità",
    passport: 'Passaporto',
    driving_license: 'Patente di guida',
    residence_permit: 'Permesso di soggiorno',
};
const CHANNEL: Record<string, string> = { sms: 'SMS', email: 'Email' };
const DOC_LABEL: Record<string, string> = { unsigned_document: 'Documento originale (da firmare)', signed_document: 'Documento firmato' };
const COMM_LABEL: Record<string, string> = { unsigned_document: 'documento da firmare', signed_document: 'copia del documento firmato', otp: 'codice OTP' };

type Tone = 'emerald' | 'amber' | 'rose' | 'slate';
const TONE: Record<Tone, string> = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
};

export const dossierEsito = (d: any): { label: string; tone: Tone } => {
    const s = String(d?.state ?? d?.current_state ?? '');
    return DOSSIER_STATE[s] || { label: s || 'Stato sconosciuto', tone: 'slate' };
};
const isSigned = (d: any) => ['signed', 'completed'].includes(String(d?.state ?? d?.current_state ?? ''));

// ── Mattoncini UI ───────────────────────────────────────────────────────────
const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 break-inside-avoid">
        <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-800 mb-4">
            <span className="text-indigo-600">{icon}</span>{title}
        </h3>
        {children}
    </section>
);
const Field: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean; wide?: boolean }> = ({ label, value, mono, wide }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div className={wide ? 'sm:col-span-2' : ''}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
            <p className={`text-sm text-slate-800 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</p>
        </div>
    );
};
const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
);
const YesNo: React.FC<{ v?: boolean; label: string }> = ({ v, label }) => v === undefined ? null : (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${v ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400 line-through'}`}>
        {label}
    </span>
);

// ── Cronologia ──────────────────────────────────────────────────────────────
type Ev = { at: string; text: string; icon: React.ReactNode; tone?: Tone };
const buildTimeline = (d: any): Ev[] => {
    const ev: Ev[] = [];
    const push = (at: string | undefined, text: string, icon: React.ReactNode, tone?: Tone) => { if (at) ev.push({ at, text, icon, tone }); };
    const recipients: any[] = d?.recipients || [];
    const who = (id: any) => {
        const r = recipients.find(x => x.id === id);
        return r ? [r.first_name, r.last_name].filter(Boolean).join(' ') : 'firmatario';
    };
    push(d?.created_at, `Pratica di firma creata${d?.user_full_name ? ` da ${d.user_full_name}` : ''}`, <FileText size={14} />);
    for (const r of recipients) {
        const n = who(r.id);
        if (r.recognized_de_visu_on || /^recognized/.test(String(r.identification_state || '')))
            push(r.identification_updated_at, `Identità di ${n} verificata${r.auditor?.fullname ? ` da ${r.auditor.fullname}` : ''}`, <IdCard size={14} />);
        push(r.notified_at, `${n} avvisato della richiesta di firma`, <Send size={14} />);
        push(r.viewed_at, `${n} ha aperto il documento`, <Eye size={14} />);
        push(r.advanced_sign_terms_accepted_at, `${n} ha accettato le condizioni della firma elettronica avanzata`, <ShieldCheck size={14} />);
        push(r.accepted_at, `${n} ha confermato di voler firmare`, <CheckCircle2 size={14} />);
        push(r.validating_at, `${n} ha inserito il codice OTP`, <Fingerprint size={14} />);
        push(r.signed_at, `${n} ha FIRMATO il documento`, <PenLine size={14} />, 'emerald');
        push(r.signed_document_sent_at, `Copia firmata inviata a ${n}`, <Mail size={14} />);
    }
    for (const c of d?.email_communications || [])
        push(c.sent_at, `Email a ${who(c.recipient_id)}: ${COMM_LABEL[c.label] || c.label}`, <Mail size={14} />);
    for (const c of d?.sms_communications || []) {
        push(c.sent_at, `SMS a ${who(c.recipient_id)}: ${COMM_LABEL[c.label] || c.label}`, <MessageSquare size={14} />);
        push(c.delivered_at, `SMS ${COMM_LABEL[c.label] || c.label} consegnato a ${who(c.recipient_id)}`, <MessageSquare size={14} />);
    }
    for (const doc of d?.documents || []) {
        if (doc.label === 'signed_document') push(doc.created_at, `Documento firmato generato (${doc.filename || 'PDF'})`, <FileCheck size={14} />, 'emerald');
    }
    for (const l of d?.conservazione_logs || [])
        push(l.created_at, `Conservazione digitale ${l.outcome === 'success' ? 'eseguita' : 'non riuscita'}${l.data?.provider ? ` su ${l.data.provider}` : ''}`,
            <Archive size={14} />, l.outcome === 'success' ? 'emerald' : 'rose');
    push(d?.state === 'completed' ? d?.updated_at : undefined, 'Pratica completata', <CheckCircle2 size={14} />, 'emerald');
    return ev.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
};

// ── Punti di firma: "Firma Mandante — pag. 5", "Parafatura — pag. 1, 2, 3" ──
const groupSignPoints = (doc: any): { label: string; pages: number[] }[] => {
    const map = new Map<string, number[]>();
    for (const sp of doc?.sign_points || []) {
        const k = sp.label || 'Firma';
        if (!map.has(k)) map.set(k, []);
        if (sp.page != null) map.get(k)!.push(Number(sp.page));
    }
    return Array.from(map.entries()).map(([label, pages]) => ({ label, pages: pages.sort((a, b) => a - b) }));
};

// ── Sezioni persona (firmatario di una pratica o della rubrica) ─────────────
/** Luogo leggibile: nome (se fornito dall'API) o, in mancanza, il codice. */
const placeOf = (r: any, prefix: string) => {
    const city = name(r?.[`${prefix}_city`]) || r?.[`${prefix}_city_code`] || '';
    const region = r?.[`${prefix}_region_code`];
    const country = name(r?.[`${prefix}_country`]) || r?.[`${prefix}_country_code`] || '';
    return [city ? (region ? `${city} (${region})` : city) : '', country].filter(Boolean).join(' · ');
};

export const PersonSections: React.FC<{ r: any }> = ({ r }) => {
    const docExpired = r.document_expire_on && new Date(r.document_expire_on) < new Date();
    const images = [
        ['Foto del volto', r.face_document_image_url],
        ['Documento — fronte', r.front_document_image_url],
        ['Documento — retro', r.back_document_image_url],
    ].filter(([, u]) => !!u) as [string, string][];
    const hasDoc = !!(r.document_type || r.document_number || images.length);
    const hasAnag = !!(r.fiscal_code || r.birthdate || r.residence_address || r.gender);
    return (
        <>
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600 mb-2 flex items-center gap-1"><User size={12} /> Dati anagrafici</p>
                <Grid>
                    <Field label="Nome e cognome" value={[r.first_name, r.last_name].filter(Boolean).join(' ')} />
                    <Field label="Codice fiscale" value={r.fiscal_code} mono />
                    <Field label="Sesso" value={r.gender === 'male' ? 'Maschio' : r.gender === 'female' ? 'Femmina' : r.gender} />
                    <Field label="Data di nascita" value={fmtDay(r.birthdate)} />
                    <Field label="Luogo di nascita" value={placeOf(r, 'birth')} />
                    <Field label="Cittadinanza" value={(name(r.citizenship) || r.citizenship_code)} />
                    <Field label="Residenza" wide value={[r.residence_address, placeOf(r, 'residence')].filter(Boolean).join(', ')} />
                </Grid>

                <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600 mt-5 mb-2 flex items-center gap-1"><Mail size={12} /> Contatti</p>
                <Grid>
                    <Field label="Email" value={r.email} />
                    <Field label="Telefono" value={r.phone_number ? `${r.phone_prefix ? `+${r.phone_prefix} ` : ''}${r.phone_number}` : ''} />
                    <Field label="Lingua" value={r.language === 'it' ? 'Italiano' : r.language} />
                </Grid>

                {hasDoc && <>
                    <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600 mt-5 mb-2 flex items-center gap-1"><IdCard size={12} /> Documento di riconoscimento</p>
                    <Grid>
                        <Field label="Tipo" value={DOC_TYPE[r.document_type] || r.document_type} />
                        <Field label="Numero" value={r.document_number ? String(r.document_number).toUpperCase() : ''} mono />
                        <Field label="Rilasciato il" value={fmtDay(r.document_issued_on)} />
                        <Field label="Rilasciato a" value={placeOf(r, 'document_issued')} />
                        <Field label="Scadenza" value={r.document_expire_on ? <span className={docExpired ? 'text-rose-700 font-bold' : ''}>{fmtDay(r.document_expire_on)}{docExpired ? ' — SCADUTO' : ''}</span> : ''} />
                        <Field label="Riconoscimento effettuato il" value={fmtDay(r.recognized_de_visu_on)} />
                        <Field label="Riconosciuto da" value={r.auditor?.fullname} />
                    </Grid>
                    {images.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2 print:hidden">
                            {images.map(([l, u]) => (
                                <a key={l} href={u} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                    <ExternalLink size={12} /> {l}
                                </a>
                            ))}
                        </div>
                    )}
                </>}

            {!hasAnag && <p className="text-xs text-slate-400 mt-2">Nessun dato anagrafico registrato.</p>}
            {!hasDoc && <p className="text-xs text-slate-400 mt-1">Nessun documento d'identità registrato.</p>}
        </>
    );
};

// ── Scheda firmatario della rubrica (modale) ────────────────────────────────
export const OtpSignerModal: React.FC<{
    signer: any; loading?: boolean; busy?: string | null;
    onClose: () => void; onClear: () => void; onDelete: () => void;
}> = ({ signer: r, loading, busy, onClose, onClear, onDelete }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    const nome = [r?.first_name, r?.last_name].filter(Boolean).join(' ') || r?.email || `Firmatario ${r?.id}`;
    return (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-stretch sm:items-center justify-center sm:p-4" onClick={onClose}>
            <div className="bg-slate-50 w-full max-w-3xl max-h-full sm:max-h-[94vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-5 border-b bg-white flex items-start gap-4">
                    <User size={32} className="shrink-0 text-indigo-600" />
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Rubrica firmatari OtpService · n. {r?.id}</p>
                        <h2 className="text-xl font-extrabold text-slate-900 break-words">{loading ? 'Caricamento…' : nome}</h2>
                    </div>
                    <button onClick={onClose} title="Chiudi" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><X size={18} /></button>
                </div>
                <div className="overflow-y-auto p-4 sm:p-6 space-y-4">
                    {!loading && (
                        <>
                            <Section icon={<User size={16} />} title="Dati del firmatario">
                                <PersonSections r={r} />
                                <Grid>
                                    <div className="sm:col-span-2 mt-4" />
                                    <Field label="Creato in rubrica il" value={fmtDateTime(r?.created_at)} />
                                    <Field label="Ultimo aggiornamento" value={fmtDateTime(r?.updated_at)} />
                                </Grid>
                            </Section>
                            <Section icon={<ShieldCheck size={16} />} title="Gestione">
                                <div className="space-y-3 text-sm text-slate-600">
                                    <p><b>Svuota dati identificativi</b>: cancella documento d'identità, foto, codice fiscale, nascita e residenza. Nome, email e telefono restano (OtpService li usa per riconoscere il firmatario). Alla prossima firma il firmatario dovrà reinserire i dati.</p>
                                    <p><b>Elimina</b>: possibile solo se il firmatario non è mai stato usato in una pratica.</p>
                                    <p className="text-xs text-slate-400">Le firme già fatte non cambiano: ogni pratica conserva la propria copia dei dati, come prova della firma.</p>
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        <button onClick={onClear} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold disabled:opacity-50 hover:bg-amber-600">
                                            {busy === `clear-${r?.id}` ? <Clock size={14} className="animate-spin" /> : <IdCard size={14} />} Svuota dati identificativi
                                        </button>
                                        <button onClick={onDelete} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-rose-700">
                                            {busy === `sdel-${r?.id}` ? <Clock size={14} className="animate-spin" /> : <X size={14} />} Elimina firmatario
                                        </button>
                                    </div>
                                </div>
                            </Section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Riepilogo compatto (es. esito della verifica) ───────────────────────────
export const OtpDossierSummary: React.FC<{ dossier: any; onOpen: () => void }> = ({ dossier: d, onOpen }) => {
    const esito = dossierEsito(d);
    const signedDoc = (d?.documents || []).find((x: any) => x.label === 'signed_document');
    const doc = signedDoc || (d?.documents || [])[0];
    return (
        <div className={`border rounded-2xl p-4 ${TONE[esito.tone]}`}>
            <div className="flex items-start gap-3">
                {isSigned(d) ? <CheckCircle2 size={28} className="shrink-0" /> : <Clock size={28} className="shrink-0" />}
                <div className="min-w-0 flex-1">
                    <p className="font-extrabold">{esito.label}</p>
                    {doc && <p className="text-sm font-semibold text-slate-800 truncate">{doc.filename}</p>}
                    <div className="mt-2 text-xs text-slate-700 space-y-0.5">
                        {(d?.recipients || []).map((r: any) => (
                            <div key={r.id}><User size={11} className="inline mr-1" />{[r.first_name, r.last_name].filter(Boolean).join(' ')}
                                {r.signed_at ? ` — firmato il ${fmtDateTime(r.signed_at)}` : ` — ${RECIPIENT_STATE[r.state] || 'non ancora firmato'}`}</div>
                        ))}
                        {d?.type && <div><ShieldCheck size={11} className="inline mr-1" />{SIGN_TYPE_LABEL[d.type] || d.type}</div>}
                    </div>
                </div>
            </div>
            <button onClick={onOpen} className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-800 text-sm font-bold hover:bg-slate-50">
                <Eye size={14} /> Apri dettagli completi
            </button>
        </div>
    );
};

// ── Scheda completa (modale) ────────────────────────────────────────────────
export const OtpDossierReportModal: React.FC<{ dossier: any; onClose: () => void }> = ({ dossier: d, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const esito = dossierEsito(d);
    const timeline = buildTimeline(d);
    const docs: any[] = d?.documents || [];
    const recipients: any[] = d?.recipients || [];
    const signedDoc = docs.find(x => x.label === 'signed_document');
    const title = signedDoc?.filename || docs[0]?.filename || `Dossier #${d?.id}`;

    return (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-stretch sm:items-center justify-center sm:p-4 print:static print:bg-white print:p-0" onClick={onClose}>
            <div className="bg-slate-50 w-full max-w-4xl max-h-full sm:max-h-[94vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden print:max-h-none print:shadow-none" onClick={e => e.stopPropagation()}>
                {/* Testata */}
                <div className={`px-6 py-5 border-b ${TONE[esito.tone]} flex items-start gap-4`}>
                    {isSigned(d) ? <CheckCircle2 size={36} className="shrink-0" /> : esito.tone === 'rose' ? <AlertTriangle size={36} className="shrink-0" /> : <Clock size={36} className="shrink-0" />}
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">Scheda della firma · pratica OtpService n. {d?.id}</p>
                        <h2 className="text-xl font-extrabold text-slate-900 break-words">{title}</h2>
                        <p className="font-bold">{esito.label}</p>
                    </div>
                    <div className="flex gap-2 shrink-0 print:hidden">
                        <button onClick={onClose} title="Chiudi" className="p-2 rounded-lg bg-white/70 hover:bg-white text-slate-700"><X size={18} /></button>
                    </div>
                </div>

                <div className="overflow-y-auto p-4 sm:p-6 space-y-4">
                    {/* Riepilogo */}
                    <Section icon={<ShieldCheck size={16} />} title="Riepilogo">
                        <Grid>
                            <Field label="Tipo di firma" value={SIGN_TYPE_LABEL[d?.type] || d?.type} wide />
                            <Field label="Pratica creata il" value={fmtDateTime(d?.created_at)} />
                            <Field label="Creata da" value={d?.user_full_name} />
                            <Field label="Firma avviata il" value={fmtDateTime(d?.signing_at, true)} />
                            <Field label="Firmata il" value={fmtDateTime(d?.signed_at, true)} />
                            <Field label="Ultimo aggiornamento" value={fmtDateTime(d?.updated_at)} />
                            <Field label="Scadenza della pratica" value={fmtDateTime(d?.dossier_expiration_date)} />
                            <Field label="Riferimento interno" value={d?.customer_contract_hash} />
                            <Field label="Fuso orario" value={d?.timezone} />
                            <Field label="Codice di verifica firma" value={d?.final_checksum} mono wide />
                            <Field label="Identificativo univoco (UUID)" value={d?.uuid} mono wide />
                        </Grid>
                    </Section>

                    {/* Cronologia */}
                    {timeline.length > 0 && (
                        <Section icon={<Clock size={16} />} title="Cronologia">
                            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
                                {timeline.map((e, i) => (
                                    <li key={i} className="ml-5">
                                        <span className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full border-2 border-white ${e.tone === 'emerald' ? 'bg-emerald-500 text-white' : e.tone === 'rose' ? 'bg-rose-500 text-white' : 'bg-indigo-100 text-indigo-600'}`}>{e.icon}</span>
                                        <p className={`text-sm ${e.tone === 'emerald' ? 'font-bold text-emerald-800' : 'text-slate-800'}`}>{e.text}</p>
                                        <p className="text-xs text-slate-500">{fmtDateTime(e.at, true)}</p>
                                    </li>
                                ))}
                            </ol>
                        </Section>
                    )}

                    {/* Firmatari */}
                    {recipients.map(r => {
                        const ident = IDENT[r.identification_state] || (r.identification_state ? { label: r.identification_state, ok: false } : null);
                        return (
                            <Section key={r.id ?? r.uuid} icon={<User size={16} />} title={`Firmatario: ${[r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || '—'}`}>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${r.signed_at ? TONE.emerald : TONE.amber}`}>{RECIPIENT_STATE[r.state] || r.state}</span>
                                    {ident && <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${ident.ok ? TONE.emerald : TONE.amber}`}>{ident.label}</span>}
                                </div>

                                <PersonSections r={r} />

                                <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600 mt-5 mb-2 flex items-center gap-1"><PenLine size={12} /> Firma</p>
                                <Grid>
                                    <Field label="Codice OTP ricevuto via" value={CHANNEL[r.otp_channel] || r.otp_channel} />
                                    <Field label="Firmato il" value={fmtDateTime(r.signed_at, true)} />
                                    <Field label="Indirizzo IP alla visualizzazione" value={r.ip_viewing} mono />
                                    <Field label="Indirizzo IP alla firma" value={r.ip_signing} mono />
                                    <Field label="Condizioni FEA accettate il" value={fmtDateTime(r.advanced_sign_terms_accepted_at, true)} />
                                    <Field label="Informativa privacy accettata il" value={fmtDay(r.privacy_terms_accepted_on)} />
                                    <Field label="Dispositivo di firma" value={r.otp_verification_source_type === 'web' ? 'Browser web' : r.otp_verification_source_type} />
                                    <Field label="Impronta della firma" value={r.checksum} mono />
                                </Grid>
                            </Section>
                        );
                    })}

                    {/* Documenti */}
                    {docs.length > 0 && (
                        <Section icon={<FileText size={16} />} title="Documenti">
                            <div className="space-y-3">
                                {docs.map(doc => {
                                    const points = groupSignPoints(doc);
                                    const signed = doc.label === 'signed_document';
                                    return (
                                        <div key={doc.id} className={`rounded-xl border p-4 ${signed ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                {signed ? <FileCheck size={18} className="text-emerald-600" /> : <FileText size={18} className="text-slate-500" />}
                                                <div className="min-w-0">
                                                    <p className="font-bold text-slate-900 text-sm break-all">{doc.filename || `Documento ${doc.id}`}</p>
                                                    <p className="text-xs text-slate-500">{DOC_LABEL[doc.label] || doc.label}</p>
                                                </div>
                                            </div>
                                            <Grid>
                                                <Field label="Pagine" value={doc.pages_count} />
                                                <Field label="Creato il" value={fmtDateTime(doc.created_at, true)} />
                                                <Field label="Inviato in conservazione il" value={fmtDateTime(doc.conservazione_digitale_at, true)} />
                                                <Field label="Impronta digitale del file (SHA-256)" value={doc.file_hash} mono wide />
                                            </Grid>
                                            {points.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Punti di firma richiesti</p>
                                                    <ul className="text-sm text-slate-700 space-y-0.5">
                                                        {points.map(p => (
                                                            <li key={p.label} className="flex items-center gap-1.5"><PenLine size={12} className="text-slate-400" />
                                                                <b>{p.label}</b> — {p.pages.length === 1 ? `pagina ${p.pages[0]}` : `pagine ${p.pages.join(', ')}`}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Section>
                    )}

                    {/* Conservazione */}
                    {(d?.conservazione_digitale_type || (d?.conservazione_logs || []).length > 0) && (
                        <Section icon={<Archive size={16} />} title="Conservazione digitale a norma">
                            {d?.conservazione_digitale_type === 'not_on_otpservice' && (d?.conservazione_logs || []).length === 0 && (
                                <p className="text-sm text-slate-600">La conservazione non è gestita su OtpService per questa pratica.</p>
                            )}
                            {(d?.conservazione_logs || []).map((l: any, i: number) => (
                                <div key={i} className={`rounded-xl border p-3 mb-2 ${l.outcome === 'success' ? TONE.emerald : TONE.rose}`}>
                                    <p className="font-bold text-sm flex items-center gap-1.5">
                                        {l.outcome === 'success' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                                        {l.outcome === 'success' ? 'Documento conservato correttamente' : 'Conservazione non riuscita'}
                                    </p>
                                    <div className="mt-2"><Grid>
                                        <Field label="Servizio di conservazione" value={l.data?.provider} />
                                        <Field label="Data" value={fmtDateTime(l.created_at, true)} />
                                        <Field label="Numero documento conservato" value={l.data?.document_id} mono />
                                        <Field label="Numero caricamento" value={l.data?.upload_id} mono />
                                    </Grid></div>
                                </div>
                            ))}
                        </Section>
                    )}

                    {/* Impostazioni di invio */}
                    <Section icon={<Send size={16} />} title="Impostazioni di invio">
                        <div className="flex flex-wrap gap-2">
                            <YesNo v={d?.send_unsigned_document_by_email} label="Documento da firmare via email" />
                            <YesNo v={d?.send_unsigned_document_by_sms} label="Documento da firmare via SMS" />
                            <YesNo v={d?.send_signed_document_by_email} label="Copia firmata via email" />
                            <YesNo v={d?.send_signed_document_by_sms} label="Copia firmata via SMS" />
                            <YesNo v={d?.otp_via_sms_allowed} label="Codice OTP via SMS" />
                            <YesNo v={d?.otp_via_email_allowed} label="Codice OTP via email" />
                            <YesNo v={d?.audit_trail_available} label="Audit trail disponibile" />
                        </div>
                    </Section>

                    <p className="text-[11px] text-slate-400 flex items-center gap-1"><MapPin size={11} /> Dati forniti da OtpService; orari espressi nel fuso {d?.timezone || 'Europe/Rome'}.</p>
                </div>
            </div>
        </div>
    );
};
